import type { Invoice, InvoiceItem } from "@/lib/api/types";

/**
 * THE CREW'S MEAL, IN ONE DOOR (D154 — T10 of the 2026-10-14 round table).
 *
 * Before this a staff meal was a takeaway the cashier opened, filled, fired,
 * then comped plate by plate under «أكل موظفين» and closed — about thirty taps
 * for food nobody pays for. The round table kept the RECORD exactly as it was
 * (an ordinary bill, every plate comped `STAFF_MEAL`, so `staffMealCost`, the
 * comp-by-reason split and every report read it unchanged) and changed only the
 * door: a «وجبة الموظفين» tile opens the bill under this name, the register's own
 * menu adds the dishes, and one button fires and comps them all.
 *
 * The bill is recognised by its CUSTOMER NAME, not by a URL flag, so a reload,
 * a second device or the back button all land on the same banner. The name is
 * stored in Arabic whatever the till's language because it is also what the
 * kitchen chit prints in the off-premise band (T19) — the grill reads it from a
 * metre away and must not bag it for a rider. No names of who ate (the panel's
 * ruling): the bill states its plate count.
 */
export const STAFF_MEAL_BILL_NAME = "وجبة الموظفين";

/**
 * The name the door wrote before the 2026-10-15 register ruling (MSA for every
 * title and action). Still recognised, so a meal opened under it the day the
 * wording changed keeps its banner; never written again.
 */
export const LEGACY_STAFF_MEAL_BILL_NAMES: readonly string[] = ["وجبة طاقم"];

/** The comp reason every plate of a staff meal is filed under (D44's list). */
export const STAFF_MEAL_REASON = "STAFF_MEAL";

/** Whether a bill's customer name is the staff-meal door's (current or legacy). */
export function isStaffMealName(name: string | null | undefined): boolean {
  const trimmed = (name ?? "").trim();
  return (
    trimmed === STAFF_MEAL_BILL_NAME || LEGACY_STAFF_MEAL_BILL_NAMES.includes(trimmed)
  );
}

/** Whether this bill is a staff meal opened through the «وجبة الموظفين» door. */
export function isStaffMealBill(
  invoice: Pick<Invoice, "channel" | "customerName" | "status">,
): boolean {
  return (
    invoice.channel === "TAKEAWAY" &&
    isStaffMealName(invoice.customerName) &&
    invoice.status === "OPEN"
  );
}

/**
 * The plates the «ابعت وجبة الطاقم» button comps, read off the bill AFTER the
 * fire: every plate the kitchen now holds that is still charged. A plate a guest
 * requested (`PENDING`) or one already comped or voided is left alone, and a
 * plate still in the basket (`DRAFT`) cannot be comped at all (D17).
 */
export function staffMealPlatesToComp(
  items: readonly Pick<InvoiceItem, "id" | "fulfillmentState" | "billingState">[],
): string[] {
  return items
    .filter(
      (item) =>
        item.billingState === "CHARGED" &&
        (item.fulfillmentState === "QUEUED" ||
          item.fulfillmentState === "PREPARING" ||
          item.fulfillmentState === "READY" ||
          item.fulfillmentState === "SERVED"),
    )
    .map((item) => item.id);
}

/**
 * Whether the bill can close now. D22 refuses a close while a plate is still
 * in the basket, requested or QUEUED — on a kitchen screen the cook has not
 * started it yet. Then the meal stays on the takeaway strip, already comped, and
 * the cashier closes it once the kitchen starts it.
 */
export function staffMealCanCloseNow(
  items: readonly Pick<InvoiceItem, "fulfillmentState">[],
): boolean {
  return !items.some(
    (item) =>
      item.fulfillmentState === "DRAFT" ||
      item.fulfillmentState === "PENDING" ||
      item.fulfillmentState === "QUEUED",
  );
}

/**
 * What the «وجبة طاقم» button would do now, or `null` when nothing is left.
 *
 * `send` — dishes in the basket, or plates the kitchen holds still charged:
 * the full fire → comp → close chain. `finish` — everything is already fired
 * and comped and only the close (and the settle at 0) is left: the case where
 * D22 held the close because a plate was still QUEUED, or the chain was
 * interrupted. The banner promises "close it from here once the kitchen starts
 * it", so that state must stay one tap — it used to disable the button and
 * leave the cashier the ordinary close and a zero settle by hand.
 */
export function staffMealNextStep(
  items: readonly Pick<InvoiceItem, "id" | "fulfillmentState" | "billingState">[],
  draftCount: number,
): "send" | "finish" | null {
  if (draftCount > 0 || staffMealPlatesToComp(items).length > 0) return "send";
  const live = items.filter((item) => item.fulfillmentState !== "VOIDED");
  if (live.length > 0 && staffMealCanCloseNow(items)) return "finish";
  return null;
}
